'use client';

import { useEffect, useState } from 'react';

import {
  isGoogleMapsLoaded,
  loadGoogleMapsAPI,
  type GoogleMapsLoaderConfig,
} from './google-maps-loader';

/**
 * Hook to load and track Google Maps API loading state
 * @param config - Configuration for the Google Maps API
 * @returns Object with loading state and error
 */
export function useGoogleMapsAPI(config: GoogleMapsLoaderConfig) {
  const [loading, setLoading] = useState(!isGoogleMapsLoaded());
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!config.apiKey) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Google Maps loader effect (external-system sync); the same `error` state is also set asynchronously from the loader callback, so it cannot be derived purely during render.
      setError(new Error('Google Maps API key is required'));
      return;
    }

    if (isGoogleMapsLoaded()) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    loadGoogleMapsAPI(config)
      .then(() => {
        setLoading(false);
        setError(null);
      })
      .catch((err) => {
        setLoading(false);
        setError(err);
      });
    // `config` is intentionally not a dependency: callers pass an inline object
    // literal recreated every render, so depending on it would re-fire this
    // loader effect in a loop. `config.apiKey` is the only meaningful trigger;
    // the loader is a module-level singleton, so the remaining config fields
    // (libraries/version/language/region) only matter on the first load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.apiKey]);

  return { loading, error, isLoaded: isGoogleMapsLoaded() };
}
